import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { TableScroll } from '../components/ui/table-scroll.js';
import {
  listReconciliation,
  resolveReconciliation,
  type ReconcileOverride,
  type ReconciliationRow,
} from '../lib/api/reconciliation.js';

/**
 * Story-window review list (plan §6, M2). Every automatic decision is logged;
 * this page shows the ones taken at low confidence — `review` (the window's
 * articles sit on more than one of the issuer's threads: ruling 3, no
 * guessing) and substance-3 filings with no closure form — until an operator
 * resolves them. Confirm is in-app (never a native confirm(): it blocks
 * automation), same pattern as ConsolidateControl.
 */

const DECISION_COLORS: Record<string, string> = {
  review: 'bg-warning-100 text-warning-700',
  new_article: 'bg-primary-100 text-primary-700',
  associate: 'bg-grey-100 text-grey-700',
  consolidate: 'bg-success-100 text-success-700',
  supersede: 'bg-purple-100 text-purple-700',
  none: 'bg-grey-100 text-grey-500',
};

const OVERRIDE_LABEL: Record<ReconcileOverride, string> = {
  new_article: 'Write article (link latest)',
  associate: 'Associate (no article)',
  dismiss: 'Dismiss',
};

export function ReconciliationPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [pending, setPending] = useState<{ id: string; override: ReconcileOverride } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['reconciliation', page, includeResolved],
    queryFn: () => listReconciliation({ page, page_size: 50, include_resolved: includeResolved }),
  });

  const resolve = useMutation({
    mutationFn: ({ id, override }: { id: string; override: ReconcileOverride }) =>
      resolveReconciliation(id, override),
    onSuccess: (data, vars) => {
      const enqueued = (data?.applied?.enqueued || []).length;
      setNotice(
        vars.override === 'new_article'
          ? enqueued
            ? `Article queued (${enqueued} job${enqueued === 1 ? '' : 's'}).`
            : 'Nothing queued — the product already has a current article or shares its target.'
          : vars.override === 'associate'
            ? 'Recorded: associate, no article.'
            : 'Dismissed.',
      );
      setPending(null);
      queryClient.invalidateQueries({ queryKey: ['reconciliation'] });
    },
    onError: (err: any) => {
      setNotice(err?.response?.data?.detail || err?.message || 'Resolve failed');
      setPending(null);
    },
  });

  const rows: ReconciliationRow[] = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-grey-900">Story-window review</h1>
          <p className="text-sm text-grey-600">
            Automatic decisions taken at low confidence. Resolve each one: write the masthead's
            article linking its latest piece, associate without an article, or dismiss.
          </p>
        </div>
        <label className="text-sm text-grey-700 flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeResolved}
            onChange={(e) => { setIncludeResolved(e.target.checked); setPage(1); }}
          />
          Show resolved
        </label>
      </div>

      {notice && (
        <div className="text-sm text-grey-800 bg-grey-50 border border-grey-200 rounded-lg px-4 py-2 flex justify-between">
          <span>{notice}</span>
          <button className="text-grey-500" onClick={() => setNotice(null)}>×</button>
        </div>
      )}

      {list.isLoading ? (
        <div className="text-sm text-grey-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-grey-500">Nothing waiting for review.</div>
      ) : (
        <TableScroll>
          <table className="min-w-full text-sm">
            <thead className="text-left text-grey-600">
              <tr>
                <th className="py-2 pr-4">When</th>
                <th className="py-2 pr-4">Filing</th>
                <th className="py-2 pr-4">Product</th>
                <th className="py-2 pr-4">Decision</th>
                <th className="py-2 pr-4">Why</th>
                <th className="py-2 pr-4">Resolve</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isPending = pending?.id === r.decision_id;
                return (
                  <tr key={r.decision_id} className="border-t border-grey-200 align-top">
                    <td className="py-2 pr-4 whitespace-nowrap text-grey-600">
                      {format(new Date(r.created_at), 'dd MMM HH:mm')}
                    </td>
                    <td className="py-2 pr-4">
                      <Link to={`/announcements/${r.announcement_id}`} className="text-primary-700 hover:underline">
                        {r.company_code ? `${r.company_code} · ` : ''}{r.announcement_title}
                      </Link>
                      <div className="text-xs text-grey-500">
                        substance {r.substance ?? '—'}
                        {r.closure_form ? ` · ${r.closure_form}` : ''}
                        {r.thread_ambiguous ? ' · two threads' : ''}
                        {r.window_dates?.length ? ` · window ${r.window_dates[0]} → ${r.window_dates[r.window_dates.length - 1]}` : ''}
                      </div>
                    </td>
                    <td className="py-2 pr-4">{r.product_name ?? '—'}</td>
                    <td className="py-2 pr-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${DECISION_COLORS[r.decision] ?? 'bg-grey-100 text-grey-700'}`}>
                        {r.decision}
                      </span>
                      {r.operator_override && (
                        <div className="text-xs text-grey-500 mt-1">resolved: {r.operator_override}</div>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-grey-700 max-w-md">{r.reason}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {r.operator_override ? (
                        <span className="text-xs text-grey-400">—</span>
                      ) : isPending ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-xs text-grey-700">{OVERRIDE_LABEL[pending!.override]}?</span>
                          <button
                            className="px-2 py-1 text-xs rounded bg-primary-600 text-white disabled:opacity-50"
                            disabled={resolve.isPending}
                            onClick={() => resolve.mutate({ id: r.decision_id, override: pending!.override })}
                          >
                            Confirm
                          </button>
                          <button className="px-2 py-1 text-xs rounded border border-grey-300" onClick={() => setPending(null)}>
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <span className="inline-flex gap-1">
                          {(['new_article', 'associate', 'dismiss'] as ReconcileOverride[]).map((o) => (
                            <button
                              key={o}
                              className="px-2 py-1 text-xs rounded border border-grey-300 hover:bg-grey-50"
                              onClick={() => setPending({ id: r.decision_id, override: o })}
                            >
                              {OVERRIDE_LABEL[o]}
                            </button>
                          ))}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}

      {total > 50 && (
        <div className="flex items-center gap-3 text-sm">
          <button className="px-2 py-1 border rounded" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
          <span>Page {page} of {Math.ceil(total / 50)}</span>
          <button className="px-2 py-1 border rounded" disabled={page * 50 >= total} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
